// NOTE: The spreadsheet types every client grids itself, client-side, through
// SheetJS (`parseSpreadsheet`) instead of asking the server for a rasterized
// page image. NSAI-6005 brought mobile onto this path: it used to send .xlsx
// through the Gotenberg→PDF→page-image chain, which rendered a spreadsheet as
// an unreadable picture of a printed A4 sheet (and failed outright whenever
// that chain hiccuped). Web has gridded .csv/.xlsx this way since NSAI-3980.
export const CSV_MIME_TYPE = "text/csv";
export const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
// NOTE: Legacy binary workbook (.xls) and the macro-enabled OOXML variant
// (.xlsm). Neither was previewable on any client before NSAI-6005 — the
// server's Gotenberg allow-list never listed them — but SheetJS reads both, so
// gridding them client-side costs nothing extra.
export const XLS_MIME_TYPE = "application/vnd.ms-excel";
export const XLSM_MIME_TYPE = "application/vnd.ms-excel.sheet.macroEnabled.12";

// NOTE: Every type the client-side grid can open. Order is irrelevant — this is
// a membership test, not a preference list.
export const SPREADSHEET_PREVIEW_MIME_TYPES: ReadonlySet<string> = new Set([
  CSV_MIME_TYPE,
  XLSX_MIME_TYPE,
  XLS_MIME_TYPE,
  XLSM_MIME_TYPE,
]);

// NOTE: Which of the above are read as DELIMITED TEXT rather than as workbook
// bytes. The split matters because the two decode paths differ per platform: a
// text file goes through a string reader, a workbook through a byte array. A
// type absent here is binary.
export const SPREADSHEET_TEXT_MIME_TYPES: ReadonlySet<string> = new Set([
  CSV_MIME_TYPE,
]);

export const isSpreadsheetPreviewMimeType = (mimeType: string): boolean =>
  SPREADSHEET_PREVIEW_MIME_TYPES.has(mimeType);

export const isSpreadsheetTextMimeType = (mimeType: string): boolean =>
  SPREADSHEET_TEXT_MIME_TYPES.has(mimeType);

// NOTE: Caps on what a client-side spreadsheet grid materialises, so a
// pathologically large file cannot blow up the view. Same figures as web's
// `SPREADSHEET_MAX_ROWS` / `SPREADSHEET_MAX_COLS`.
export const SPREADSHEET_PREVIEW_MAX_ROWS = 500;
export const SPREADSHEET_PREVIEW_MAX_COLS = 100;

// NOTE: NSAI-4326 — ceiling on a file a client will even attempt to grid
// itself. The row/column caps above bound what is *materialised*, not what is
// read: they only take effect once the whole file has been downloaded and
// decoded, so a pathologically large file still costs several full-file copies
// before the first row is dropped. Past this the clients leave the file
// download-only. The same figure doubles as a character bound for the .csv
// parser, which is conservative: UTF-8 text of N bytes is never more than N
// UTF-16 code units.
export const SPREADSHEET_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;

// NOTE: NSAI-6005 — how many sheets of a workbook the preview converts to rows.
// Bounds `sheet_to_json` (the per-cell cost) on a workbook with dozens of tabs,
// while still letting a normal multi-sheet file be browsed tab by tab.
export const SPREADSHEET_PREVIEW_MAX_SHEETS = 10;

// NOTE: NSAI-6114 — how many full sheets the cross-sheet cell budget below
// holds. Enough for any workbook a person browses tab by tab, while a sheet
// past the budget is left out and reported as not shown.
const SPREADSHEET_PREVIEW_FULL_SHEETS_IN_BUDGET = 5;

// NOTE: NSAI-6114 — the cells a preview may materialise across ALL the sheets
// it converts. The row/column caps above are per sheet, so on their own they
// bound nothing in total: the sheet dimension multiplies them. Sized in full
// sheets at the caps PLUS one row, because web parses one sentinel row past
// the row cap to detect truncation while mobile does not: budgeting on the bare
// 500 rows would let mobile fit five dense sheets and web only four, and the
// two clients would show the same file with different tabs (250,500 cells).
export const SPREADSHEET_PREVIEW_MAX_TOTAL_CELLS =
  SPREADSHEET_PREVIEW_FULL_SHEETS_IN_BUDGET *
  (SPREADSHEET_PREVIEW_MAX_ROWS + 1) *
  SPREADSHEET_PREVIEW_MAX_COLS;

// NOTE: NSAI-6114 — ceilings on a zip-based workbook's (.xlsx/.xlsm) central
// directory, checked BEFORE SheetJS runs. SheetJS inflates every entry of the
// archive eagerly, before any sheet, row or cell cap can apply, so the byte
// ceiling above bounds only the compressed size: a 2 MiB file can declare tens
// of thousands of sheet parts, or inflate far past its size. A real workbook
// that fits the byte ceiling sits well inside both.
export const SPREADSHEET_PREVIEW_MAX_ARCHIVE_ENTRIES = 1_000;
export const SPREADSHEET_PREVIEW_MAX_UNCOMPRESSED_BYTES = 64 * 1024 * 1024;
